import * as React from 'react'
import { AlertTriangle, Film, ImagePlus, Loader2, Star } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { apiFetch } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { UnifiedAsset } from '@/types'

interface Props {
  asset: UnifiedAsset
  onClick: () => void
  onStarChange?: (assetId: number, isStarred: boolean) => void
}

export function UnifiedAssetCard({ asset, onClick, onStarChange }: Props) {
  const [optimisticStar, setOptimisticStar] = React.useState<boolean | null>(null)
  const [pendingStar, setPendingStar] = React.useState(false)

  const canStar = asset.sourceType === 'asset'
  const isStarred = optimisticStar ?? asset.isStarred ?? false

  const handleStarClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canStar || pendingStar) return
    const next = !isStarred
    setOptimisticStar(next)
    setPendingStar(true)
    try {
      const url = `/api/v1/assets/${asset.sourceId}/${next ? 'star' : 'unstar'}`
      const res = await apiFetch(url, { method: 'POST' })
      if (!res.ok) throw new Error(await res.text())
      onStarChange?.(asset.sourceId, next)
    } catch {
      // Rollback on failure
      setOptimisticStar(!next)
    } finally {
      setPendingStar(false)
    }
  }

  const isImage = asset.fileType === 'image'
  const isReference = asset.sourceType === 'reference'

  // For URL-based references (source_url only, no local file), we have nothing
  // servable as a video src — fall through to the Film icon placeholder instead.
  const hasLocalFile = isReference
    ? !!(asset.originalReference?.thumbnail_path || asset.originalReference?.local_path)
    : !!asset.originalAsset

  const thumbnailSrc = hasLocalFile
    ? isReference
      ? asset.originalReference?.thumbnail_path
        ? `/uploads/${asset.originalReference.thumbnail_path}`
        : `/uploads/${asset.originalReference!.local_path}`
      : `/uploads/${asset.originalAsset!.file_path}`
    : null

  const isAnalyzing =
    asset.analysisStatus === 'pending' || asset.analysisStatus === 'processing'
  const isFailed = asset.analysisStatus === 'failed'

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full text-left rounded-lg border border-border bg-card overflow-hidden',
        'transition-all duration-150 hover:ring-2 hover:ring-primary focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary',
      )}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {thumbnailSrc ? (
          isImage ? (
            <img
              src={thumbnailSrc}
              alt={asset.name}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <video
              src={thumbnailSrc}
              className="absolute inset-0 w-full h-full object-cover"
              preload="metadata"
              muted
              playsInline
            />
          )
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            {isImage ? (
              <ImagePlus className="h-8 w-8 opacity-40" />
            ) : (
              <Film className="h-8 w-8 opacity-40" />
            )}
          </div>
        )}

        {/* Analysis status — top-left */}
        {(isAnalyzing || isFailed) && (
          <div className="absolute top-1.5 left-1.5">
            {isAnalyzing ? (
              <Loader2 className="h-4 w-4 animate-spin text-white drop-shadow" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-destructive drop-shadow" />
            )}
          </div>
        )}

        {/* File type badge — top-right */}
        <Badge
          variant="secondary"
          className="absolute top-1.5 right-1.5 text-[10px] uppercase tracking-wide px-1.5 py-0 bg-black/60 text-white border-0 rounded"
        >
          {isImage ? 'Image' : 'Video'}
        </Badge>

        {/* Star button — bottom-right. Only on assets (references can't be starred). */}
        {canStar && (
          <button
            type="button"
            onClick={handleStarClick}
            disabled={pendingStar}
            className={cn(
              'absolute bottom-1.5 right-1.5 inline-flex items-center justify-center',
              'h-6 w-6 rounded bg-black/60 text-white border border-white/20',
              'transition-colors hover:bg-black/80 disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            )}
            aria-label={isStarred ? 'Unstar asset' : 'Star asset'}
            aria-pressed={isStarred}
          >
            <Star
              className={cn(
                'h-3.5 w-3.5',
                isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-white/80',
              )}
            />
          </button>
        )}

        {/* REF badge — bottom-left */}
        {isReference && (
          <Badge
            variant="outline"
            className="absolute bottom-1.5 left-1.5 text-[10px] uppercase tracking-wide px-1.5 py-0 bg-black/60 text-white border-white/30 rounded"
          >
            Ref
          </Badge>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 space-y-1">
        <p className="text-sm font-medium leading-tight truncate" title={asset.name}>
          {asset.name}
        </p>

        <div className="flex items-center gap-1 flex-wrap">
          {asset.folderName ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 truncate max-w-[120px]">
              {asset.folderName}
            </Badge>
          ) : isReference ? (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              Reference
            </Badge>
          ) : null}
        </div>

        {asset.aiDescription && typeof asset.aiDescription === 'string' && (
          <p
            className="text-xs text-muted-foreground truncate leading-snug"
            title={asset.aiDescription}
          >
            {asset.aiDescription}
          </p>
        )}
      </div>
    </button>
  )
}
